import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { apiTokenService, type ApiTokenView } from '../../services/api-tokens';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
  CopyableValue,
} from '../../components/common';
import { formatDateTime, formatRelativeTime } from '../../utils/formatters';
import { TOKEN_BADGE_CLASS, describeTokenStatus } from './tokenStatus';
import { useExpiryClock } from './useExpiryClock';

export function ApiTokensTab() {
  const { tenantId, tenants } = useTenant();
  // A token carries only the creator's MEMBERSHIP here (global roles do not
  // ride along), so a superadmin who entered this tenant from Platform admin
  // would mint one that can never work. The API refuses it too; this just
  // says so up front instead of offering a button that fails.
  // A management grant (#16) is not a membership either: the API refuses a token for a delegated actor.
  const membership = tenants.find((t) => t.tenantId === tenantId);
  const isMember = membership !== undefined && !membership.delegated;
  const viaGrant = membership?.delegated !== undefined;
  const [showCreate, setShowCreate] = useState(false);
  const [mintedToken, setMintedToken] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  /** Revokes done in this view: the row flips in place (no reload, no spinner) and the announcement
   *  below says what happened. Keyed by token id -> the instant it was revoked. */
  const [revokedHere, setRevokedHere] = useState<ReadonlyMap<string, string>>(new Map());
  const [revoking, setRevoking] = useState<ReadonlySet<string>>(new Set());
  const [announcement, setAnnouncement] = useState<string | null>(null);

  // Per-tenant view state: leaving a tenant drops it, and a revoke still in flight for the tenant we left
  // must not announce itself (or flip a row) in the one we are now looking at.
  const activeTenant = useRef(tenantId);
  useEffect(() => {
    activeTenant.current = tenantId;
    setRevokedHere(new Map());
    setRevoking(new Set());
    setAnnouncement(null);
    setActionError(null);
  }, [tenantId]);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      apiTokenService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<ApiTokenView>(fetchPage, 'all');
  // Re-render when the next still-active token expires, so its row flips to Expired on time (#95).
  const now = useExpiryClock(
    list.items.flatMap((t) => (!t.disabledAt && !revokedHere.has(t.id) && t.expiresAt ? [t.expiresAt] : []))
  );

  const remove = async (token: ApiTokenView) => {
    if (!tenantId) return;
    if (!window.confirm(`Revoke token ${token.name}? Scripts using it stop working.`)) {
      return;
    }
    setActionError(null);
    setAnnouncement(null);
    setRevoking((prev) => new Set(prev).add(token.id));
    try {
      await apiTokenService.remove(tenantId, token.id);
      if (activeTenant.current !== tenantId) return;
      setRevokedHere((prev) => new Map(prev).set(token.id, new Date().toISOString()));
      setAnnouncement(`Token “${token.name}” revoked. Scripts using it no longer work.`);
    } catch (err) {
      if (activeTenant.current === tenantId) setActionError(err);
    } finally {
      if (activeTenant.current === tenantId) setRevoking((prev) => {
        const next = new Set(prev);
        next.delete(token.id);
        return next;
      });
    }
  };

  return (
    <div>
      <div className="page-actions tab-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!isMember}
          aria-describedby={isMember ? undefined : 'token-nonmember-note'}
          onClick={() => setShowCreate(true)}
        >
          New token
        </button>
      </div>
      {!isMember && (
        <p className="notice" id="token-nonmember-note" role="note">
          {viaGrant
            ? 'API tokens can only be created by members of this organization. You manage it through management access, so you can’t create one here.'
            : 'API tokens act with your membership in this organization; you’re viewing it as a platform admin, so you can’t create one here. Tokens that already exist can still be revoked.'}
        </p>
      )}
      {actionError !== null && <ErrorState error={actionError} />}
      {/* Stable live region: present before its content changes, so screen readers announce it. */}
      <div aria-live="polite" aria-atomic="true" data-testid="revoke-announcement">
        {announcement && <p className="notice">{announcement}</p>}
      </div>
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No API tokens"
          description="Tokens authenticate scripts against the public REST API, scoped to this tenant."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Last used</th>
                <th>Expires</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.items.map((item) => {
                // A revoke done in this view shows immediately, ahead of any refetch.
                const t: ApiTokenView =
                  !item.disabledAt && revokedHere.has(item.id)
                    ? { ...item, disabledAt: revokedHere.get(item.id)!, disabledReason: 'manual' }
                    : item;
                const status = describeTokenStatus(t, now);
                return (
                  <tr key={t.id} className={status.canRevoke ? undefined : 'row-disabled'}>
                    <td>{t.name}</td>
                    <td>
                      <code>{t.tokenPrefix}…</code>
                    </td>
                    <td>{t.lastUsedAt ? formatRelativeTime(t.lastUsedAt) : 'never'}</td>
                    <td>{t.expiresAt ? formatDateTime(t.expiresAt) : 'no expiry'}</td>
                    <td>
                      <span className={`status-badge ${TOKEN_BADGE_CLASS[status.state]}`}>{status.badge}</span>
                      {status.reason && <div className="text-muted">{status.reason}</div>}
                    </td>
                    <td className="row-actions">
                      {status.canRevoke && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={revoking.has(t.id)}
                          onClick={() => void remove(t)}
                        >
                          {revoking.has(t.id) ? 'Revoking…' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />

      {showCreate && (
        <CreateTokenModal
          onClose={() => setShowCreate(false)}
          onCreated={(token) => {
            setShowCreate(false);
            setMintedToken(token);
            void list.reload();
          }}
        />
      )}
      {mintedToken && (
        <Modal title="API token" onClose={() => setMintedToken(null)}>
          <p>
            <strong>This token is shown only once.</strong> Store it in a secret
            manager — it cannot be retrieved again.
          </p>
          <CopyableValue value={mintedToken} label="Copy API token" />
        </Modal>
      )}
    </div>
  );
}

function CreateTokenModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (token: string) => void;
}) {
  const { tenantId } = useTenant();
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await apiTokenService.create(tenantId, {
        name,
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      });
      onCreated(result.token);
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <Modal title="New API token" onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="token-name">Name (what uses it?)</label>
          <input
            id="token-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="token-expiry">Expiry (optional)</label>
          <input
            id="token-expiry"
            name="expiresAt"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create token'}
        </button>
      </form>
    </Modal>
  );
}
